/** Presentation helpers shared by the Projects pages, dialogs and dashboard widgets. */
import i18n from '../../i18n/config';
import { PROJECT_STATUSES, type Project, type ProjectStatus } from '../../types/projects';

const locale = (): string => (i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR');

const asDate = (iso?: string | null): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Small uppercase card title (« FICHIERS DU PROJET »), shared by the project cards. */
export const OVERLINE = 'text-[11px] font-medium uppercase tracking-wide text-muted-foreground';

/** "12 septembre 2026" */
export const formatDay = (iso?: string | null): string =>
  asDate(iso)?.toLocaleDateString(locale(), { day: 'numeric', month: 'long', year: 'numeric' }) ?? '—';

/** "12 sept." (the year only when it is not the current one) */
export function formatShortDay(iso?: string | null): string {
  const d = asDate(iso);
  if (!d) return '';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(locale(), { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
}

/** "12 sept. 2026, 09:19" */
export const formatDateTime = (iso?: string | null): string =>
  asDate(iso)?.toLocaleString(locale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }) ?? '—';

/** Compact relative time ("il y a 3 j"), localised by Intl. */
export function timeAgo(iso?: string | null): string {
  const d = asDate(iso);
  if (!d) return '';
  const seconds = Math.round((d.getTime() - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale(), { numeric: 'auto', style: 'short' });
  const abs = Math.abs(seconds);
  if (abs < 60) return rtf.format(seconds, 'second');
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(seconds / 3600), 'hour');
  if (abs < 86400 * 30) return rtf.format(Math.round(seconds / 86400), 'day');
  if (abs < 86400 * 365) return rtf.format(Math.round(seconds / (86400 * 30)), 'month');
  return rtf.format(Math.round(seconds / (86400 * 365)), 'year');
}

const SIZE_UNITS = ['B', 'KB', 'MB', 'GB'] as const;

export function formatSize(bytes?: number | null): string {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < SIZE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return unit === 0 ? `${bytes} B` : `${value.toFixed(value >= 10 ? 0 : 1)} ${SIZE_UNITS[unit]}`;
}

/** Last activity of a project (falls back to its creation). */
export const lastActivity = (p: Project): string => p.updated_at ?? p.created_at;

const timeOf = (iso?: string | null): number => asDate(iso)?.getTime() ?? 0;

/** Most recent activity first. */
export const byLastActivity = (a: Project, b: Project): number =>
  timeOf(lastActivity(b)) - timeOf(lastActivity(a));

export const authorName = (p: { author?: { name?: string | null; email?: string | null } | null }): string =>
  p.author?.name || p.author?.email || '';

/** Stable hue (0–360) from any string: a project or a domain always gets the same colour. */
export function hueOf(text: string): number {
  let h = 0;
  for (const c of text) h = (h * 31 + c.charCodeAt(0)) % 360;
  return h;
}

/* --------------------------------- format --------------------------------- */

/** Deposit formats, from the main file's extension (« none »: no main file). */
export const PROJECT_FORMATS = ['pdf', 'html', 'archive', 'docx', 'pptx', 'xlsx', 'md', 'code', 'other', 'none'] as const;
export type ProjectFormat = (typeof PROJECT_FORMATS)[number];

const FORMAT_BY_EXT: Record<string, ProjectFormat> = {
  pdf: 'pdf',
  html: 'html',
  htm: 'html',
  zip: 'archive',
  rar: 'archive',
  '7z': 'archive',
  tar: 'archive',
  gz: 'archive',
  tgz: 'archive',
  docx: 'docx',
  doc: 'docx',
  pptx: 'pptx',
  ppt: 'pptx',
  xlsx: 'xlsx',
  xls: 'xlsx',
  md: 'md',
  markdown: 'md',
};

const CODE_EXT = new Set([
  'ts', 'tsx', 'js', 'jsx', 'mjs', 'cjs', 'py', 'ipynb', 'json', 'css', 'scss', 'sql', 'sh',
  'yaml', 'yml', 'java', 'go', 'rb', 'php', 'c', 'cpp', 'cs', 'rs', 'kt', 'swift', 'vue', 'svelte',
]);

/** The project's format: the main file's extension (no backend lookup: the list carries `main_file`). */
export function formatOf(p: Pick<Project, 'main_file'>): ProjectFormat {
  const path = p.main_file?.trim();
  if (!path) return 'none';
  const name = path.slice(path.lastIndexOf('/') + 1);
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1).toLowerCase() : '';
  if (!ext) return 'other';
  return FORMAT_BY_EXT[ext] ?? (CODE_EXT.has(ext) ? 'code' : 'other');
}

/** Tag hue per format; null: neutral tag. */
export const FORMAT_HUE: Record<ProjectFormat, number | null> = {
  pdf: 0,
  html: 24,
  archive: 268,
  docx: 220,
  pptx: 340,
  xlsx: 142,
  md: 192,
  code: 46,
  other: null,
  none: null,
};

/* -------------------------------- grouping -------------------------------- */

export type ProjectGroupBy = 'none' | 'domain' | 'format' | 'status' | 'date';

export const DATE_BUCKETS = ['today', 'yesterday', 'week', 'month', 'older'] as const;
export type DateBucket = (typeof DATE_BUCKETS)[number];

export function dateBucket(iso?: string | null): DateBucket {
  const d = asDate(iso);
  if (!d) return 'older';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const day = 24 * 60 * 60 * 1000;
  const then = d.getTime();
  if (then >= startOfToday) return 'today';
  if (then >= startOfToday - day) return 'yesterday';
  if (then >= startOfToday - 6 * day) return 'week';
  if (then >= startOfToday - 29 * day) return 'month';
  return 'older';
}

export interface ProjectGroup {
  /** Stable key (domain id, format, status, bucket); null for the single ungrouped list. */
  key: string | null;
  label: string | null;
  items: Project[];
}

/**
 * Ordered groups: domains alphabetically, formats, statuses and dates in
 * their fixed order. `labelOf` translates format, status and date keys.
 */
export function groupProjects(
  projects: Project[],
  by: ProjectGroupBy,
  labelOf: (by: 'format' | 'status' | 'date', key: string) => string,
  noDomainLabel: string
): ProjectGroup[] {
  if (by === 'none') return [{ key: null, label: null, items: projects }];

  const keyOf = (p: Project): string =>
    by === 'domain'
      ? p.domain_id
      : by === 'format'
        ? formatOf(p)
        : by === 'status'
          ? p.status
          : dateBucket(lastActivity(p));

  const buckets = new Map<string, { label: string; items: Project[] }>();
  for (const p of projects) {
    const key = keyOf(p);
    const label = by === 'domain' ? p.domain_name?.trim() || noDomainLabel : labelOf(by, key);
    const bucket = buckets.get(key);
    if (bucket) bucket.items.push(p);
    else buckets.set(key, { label, items: [p] });
  }

  const keys = [...buckets.keys()];
  if (by === 'status') {
    keys.sort(
      (a, b) => PROJECT_STATUSES.indexOf(a as ProjectStatus) - PROJECT_STATUSES.indexOf(b as ProjectStatus)
    );
  } else if (by === 'format') {
    keys.sort((a, b) => PROJECT_FORMATS.indexOf(a as ProjectFormat) - PROJECT_FORMATS.indexOf(b as ProjectFormat));
  } else if (by === 'date') {
    keys.sort((a, b) => DATE_BUCKETS.indexOf(a as DateBucket) - DATE_BUCKETS.indexOf(b as DateBucket));
  } else {
    keys.sort((a, b) => buckets.get(a)!.label.localeCompare(buckets.get(b)!.label, locale()));
  }
  return keys.map((key) => {
    const bucket = buckets.get(key)!;
    return { key, label: bucket.label, items: bucket.items };
  });
}

/** Case-insensitive match on title, texts, domain and author. */
export function matchesSearch(p: Project, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [p.title, p.brief, p.purpose, p.domain_name, authorName(p)]
    .filter((v): v is string => !!v)
    .some((v) => v.toLowerCase().includes(q));
}
